/**
 * Notification Service for Mangu CRM
 * Handles browser notifications and sound alerts for new messages
 */

class NotificationService {
    constructor() {
        this.notificationsEnabled = false;
        this.soundEnabled = true;
        this.audioContext = null;
        this.notificationSound = null;

        // Initialize audio context on user interaction
        this.initAudio();

        // Load preferences from localStorage
        this.loadPreferences();
    }

    /**
     * Initialize Web Audio API for notification sounds
     */
    initAudio() {
        try {
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        } catch (e) {
            console.warn('Web Audio API not supported');
        }
    }

    /**
     * Request permission for desktop notifications
     */
    async requestPermission() {
        if (!('Notification' in window)) {
            console.warn('Browser does not support notifications');
            return false;
        }

        if (Notification.permission === 'granted') {
            this.notificationsEnabled = true;
            return true;
        }

        if (Notification.permission !== 'denied') {
            const permission = await Notification.requestPermission();
            this.notificationsEnabled = permission === 'granted';
            this.savePreferences();
            return this.notificationsEnabled;
        }

        return false;
    }

    /**
     * Play notification sound using Web Audio API
     */
    playNotificationSound() {
        if (!this.soundEnabled || !this.audioContext) return;

        try {
            // Resume audio context if suspended (Chrome autoplay policy)
            if (this.audioContext.state === 'suspended') {
                this.audioContext.resume();
            }

            const oscillator = this.audioContext.createOscillator();
            const gainNode = this.audioContext.createGain();

            oscillator.connect(gainNode);
            gainNode.connect(this.audioContext.destination);

            // Create a pleasant notification sound (two-tone beep)
            oscillator.frequency.value = 800; // First tone: 800Hz
            oscillator.type = 'sine';

            // Volume envelope
            gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
            gainNode.gain.linearRampToValueAtTime(0.3, this.audioContext.currentTime + 0.01);
            gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.15);

            oscillator.start(this.audioContext.currentTime);
            oscillator.stop(this.audioContext.currentTime + 0.15);

            // Second tone after a short delay
            setTimeout(() => {
                const oscillator2 = this.audioContext.createOscillator();
                const gainNode2 = this.audioContext.createGain();

                oscillator2.connect(gainNode2);
                gainNode2.connect(this.audioContext.destination);

                oscillator2.frequency.value = 1000; // Second tone: 1000Hz
                oscillator2.type = 'sine';

                gainNode2.gain.setValueAtTime(0, this.audioContext.currentTime);
                gainNode2.gain.linearRampToValueAtTime(0.3, this.audioContext.currentTime + 0.01);
                gainNode2.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.15);

                oscillator2.start(this.audioContext.currentTime);
                oscillator2.stop(this.audioContext.currentTime + 0.15);
            }, 100);

        } catch (error) {
            console.error('Error playing notification sound:', error);
        }
    }

    /**
     * Show desktop notification
     */
    showNotification(title, options = {}) {
        if (!this.notificationsEnabled || Notification.permission !== 'granted') {
            return null;
        }

        const defaultOptions = {
            icon: '/logo.png',
            badge: '/favi.png',
            tag: 'mangu-message',
            requireInteraction: false,
            ...options
        };

        try {
            const notification = new Notification(title, defaultOptions);

            // Auto-close after 5 seconds
            setTimeout(() => notification.close(), 5000);

            return notification;
        } catch (error) {
            console.error('Error showing notification:', error);
            return null;
        }
    }

    /**
     * Show notification for new message
     */
    notifyNewMessage(contactName, messageText, conversationId) {
        // Play sound
        this.playNotificationSound();

        // Show desktop notification
        const notification = this.showNotification(
            `💬 Nuevo mensaje de ${contactName}`,
            {
                body: messageText?.substring(0, 100) || 'Nuevo mensaje',
                icon: '/logo.png',
                tag: `conversation-${conversationId}`,
                data: { conversationId }
            }
        );

        // Handle notification click
        if (notification) {
            notification.onclick = () => {
                window.focus();
                notification.close();
                // Dispatch custom event to open conversation
                window.dispatchEvent(new CustomEvent('openConversation', {
                    detail: { conversationId }
                }));
            };
        }

        return notification;
    }

    /**
     * Toggle sound on/off
     */
    toggleSound(enabled) {
        this.soundEnabled = enabled;
        this.savePreferences();
    }

    /**
     * Toggle notifications on/off
     */
    async toggleNotifications(enabled) {
        if (enabled) {
            const permitted = await this.requestPermission();
            this.notificationsEnabled = permitted;
        } else {
            this.notificationsEnabled = false;
        }
        this.savePreferences();
        return this.notificationsEnabled;
    }

    /**
     * Save preferences to localStorage
     */
    savePreferences() {
        localStorage.setItem('mangu_notifications', JSON.stringify({
            soundEnabled: this.soundEnabled,
            notificationsEnabled: this.notificationsEnabled
        }));
    }

    /**
     * Load preferences from localStorage
     */
    loadPreferences() {
        try {
            const prefs = JSON.parse(localStorage.getItem('mangu_notifications'));
            if (prefs) {
                this.soundEnabled = prefs.soundEnabled !== false;
                this.notificationsEnabled = prefs.notificationsEnabled && Notification.permission === 'granted';
            }
        } catch (e) {
            // Use defaults
        }
    }

    /**
     * Test notification (for settings)
     */
    testNotification() {
        this.playNotificationSound();
        this.showNotification('🔔 Prueba de Notificación', {
            body: 'Las notificaciones están funcionando correctamente',
            icon: '/logo.png'
        });
    }
}

// Export singleton instance
export const notificationService = new NotificationService();
